import {
  Box,
  ConditionalValue,
  Dialog,
  Portal,
  CloseButton,
} from '@chakra-ui/react';
import React from 'react';

type DialogSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'cover';

interface DialogComponentProps {
  isOpen: boolean;
  onClose?: () => void;
  size?: ConditionalValue<DialogSize>;
  closeOnOverlayClick?: boolean;
  isFullHeight?: boolean;
  childrenMayOverflow?: boolean;
  children?: React.ReactElement;
  headingText?: string;
  isShowClose?: boolean;
  isCentered?: boolean;
  stylesProps?: React.CSSProperties;
  e2eTitle: string;
  onOverlayClick?: (event: Event) => void;
  headerBorder?: boolean;
  width?: string;
  closeCSS?: React.CSSProperties;
}

export const DialogComponent: React.FC<DialogComponentProps> = ({
  closeOnOverlayClick = true,
  isOpen,
  onClose,
  size = 'xl',
  isFullHeight = false,
  childrenMayOverflow = false,
  children,
  headingText,
  isShowClose = true,
  isCentered = true,
  stylesProps,
  e2eTitle,
  headerBorder = false,
  onOverlayClick,
  width,
  closeCSS,
}) => {
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details: any) => {
        if (!details.open) {
          if (onClose) {
            onClose();
          }
        }
      }}
      closeOnInteractOutside={closeOnOverlayClick}
      onPointerDownOutside={(event: any) => {
        // Stop propagation to prevent parent drawers from also closing
        event.stopPropagation();
        if (onOverlayClick) {
          onOverlayClick(event);
        }
      }}
      onInteractOutside={(event: any) => {
        // Stop propagation to prevent parent drawers from also closing
        event.stopPropagation();
      }}
      placement={isCentered ? 'center' : undefined}
      size={size}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner
          height={isFullHeight ? '100vh' : undefined}
          p={isCentered && !isFullHeight ? '37px 0' : undefined}
        >
          <Dialog.Content
            style={stylesProps}
            bgColor={'surface.primary'}
            border="1"
            borderColor={'border.secondary'}
            data-e2e={e2eTitle}
            padding={'0px'}
            width={width ? width : '472px'}
            maxWidth={width ? width : '472px'}
            borderRadius={'lg'}
            position={'relative'}
            height={isFullHeight ? '100%' : undefined}
            display="flex"
            flexDirection="column"
          >
            {headingText && (
              <Dialog.Header
                fontSize={'16px'}
                textAlign={'start'}
                fontWeight={600}
                color={'text.primary'}
                padding={'20px 20px 20px 20px'}
                minHeight={'60px'}
                borderBottom={headerBorder ? '1px solid' : 'none'}
                borderColor={'border.secondary'}
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <Dialog.Title>{headingText}</Dialog.Title>
              </Dialog.Header>
            )}

            {isShowClose && (
              <Dialog.CloseTrigger asChild>
                <CloseButton
                  bgColor={'surface.primary'}
                  color={'text.secondary'}
                  _hover={{
                    border: 'none',
                    bg: 'transparent',
                    color: 'text.primary',
                  }}
                  border={'none'}
                  right={'20px'}
                  top={'18px'}
                  height={'24px'}
                  width={'24px'}
                  zIndex={'docked'}
                  style={closeCSS}
                  _focusVisible={{
                    outline: '2px solid var(--chakra-colors-border-action)',
                    outlineOffset: '2px',
                  }}
                  position={'absolute'}
                />
              </Dialog.CloseTrigger>
            )}

            <Dialog.Body
              p={'0px'}
              height={isFullHeight ? '100%' : 'auto'}
              overflowY={'auto'}
              css={{ scrollbarGutter: 'stable' }}
              data-e2e="modalBody"
              width={'100%'}
              flex={isFullHeight || childrenMayOverflow ? '1' : undefined}
            >
              <Box
                borderColor={'border.secondary'}
                height="100%"
                p={'0px 24px 24px 24px'}
              >
                {children}
              </Box>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
